"use client";

import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from "react";
import { getAssistantOptions, updateAssistantOptions } from "@/lib/storage";

// Per-thread "as of" date overrides for individual MCP servers (today: ASPI CZ,
// ASPI SK, Jogtár — anything with `dateFilter: true` in the backend config).
// State shape: { [serverName]: "YYYY-MM-DD" }. Persisted to localStorage under
// the thread-scoped assistantOptions blob so a refresh / thread switch restores it.
// The composer reads from here at send-time (buildConstraintAppendix) and the
// chip's filter popover writes to it.

type McpServerDateFiltersContextValue = {
  dateFilters: Record<string, string>;
  setDateFilter: (server: string, date: string) => void;
  clearDateFilter: (server: string) => void;
  setThreadId: (threadId: string | undefined) => void;
};

const McpServerDateFiltersContext = createContext<McpServerDateFiltersContextValue>({
  dateFilters: {},
  setDateFilter: () => {},
  clearDateFilter: () => {},
  setThreadId: () => {},
});

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function McpServerDateFiltersProvider({ children }: { children: ReactNode }) {
  const [dateFilters, setDateFilters] = useState<Record<string, string>>({});
  const threadIdRef = useRef<string | undefined>(undefined);

  const setThreadId = useCallback((id: string | undefined) => {
    threadIdRef.current = id;
    const stored = getAssistantOptions(id);
    setDateFilters(stored.mcpServerDateFilters ?? {});
  }, []);

  const setDateFilter = useCallback((server: string, date: string) => {
    if (!ISO_DATE_RE.test(date)) return;
    setDateFilters(prev => {
      if (prev[server] === date) return prev;
      const next = { ...prev, [server]: date };
      updateAssistantOptions(threadIdRef.current, { mcpServerDateFilters: next });
      return next;
    });
  }, []);

  const clearDateFilter = useCallback((server: string) => {
    setDateFilters(prev => {
      if (!(server in prev)) return prev;
      const next = { ...prev };
      delete next[server];
      updateAssistantOptions(threadIdRef.current, { mcpServerDateFilters: next });
      return next;
    });
  }, []);

  return (
    <McpServerDateFiltersContext.Provider value={{ dateFilters, setDateFilter, clearDateFilter, setThreadId }}>
      {children}
    </McpServerDateFiltersContext.Provider>
  );
}

export function useMcpServerDateFilters() {
  return useContext(McpServerDateFiltersContext);
}
